import { ArrowRight, Bot, Globe, MapPin, MessageSquareText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { routeToIntake } from '../lib/intakeRouting';

// Implements storyboard_s4_mtm_v3 logic with the ratified public name and violet
// treatment (Visual Contract §6, §10.4). Phone / message / proof mechanic preferred.
const outputs: Array<{ label: string; icon: LucideIcon }> = [
  { label: 'Website project evidence', icon: Globe },
  { label: 'Social & local updates', icon: MessageSquareText },
  { label: 'Map / local profile update', icon: MapPin },
  { label: 'AI-readable context', icon: Bot },
];

export function MessageToMarket() {
  return (
    <section id="message-to-market" className="w-full bg-obsidian py-24 sm:py-28" data-aeo-section="message-to-market">
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:px-8">
        {/* Ratified proof-stream visual */}
        <div className="relative">
          <div className="absolute -inset-4 -z-10 bg-violet/15 blur-2xl" aria-hidden="true" />
          <img
            src="/assets/storyboard_s4_mtm_v3.webp"
            alt="A phone showing a client message with a finished landscaping project, beside the line: you've done the work, now let everyone see — Message to Market."
            className="w-full select-none rounded-sm border border-violet/30"
            loading="lazy"
          />
        </div>

        <div className="max-w-xl">
          <p className="text-sm font-black uppercase tracking-wide text-violet">Message-to-Market by Macellum</p>
          <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight text-chalk sm:text-5xl">
            You've done the work. Now let everyone see.
          </h2>
          <p className="mt-6 text-lg leading-8 text-ash">
            Send proof once — a photo, a voice note, a few job details. Macellum turns it into
            visible, citable, sales-ready market presence across every surface that matters.
          </p>

          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {outputs.map((output) => {
              const Icon = output.icon;
              return (
                <li
                  key={output.label}
                  className="flex items-center gap-3 border border-violet/25 bg-violet/[0.06] px-4 py-3 text-sm font-semibold text-chalk"
                >
                  <Icon className="size-5 shrink-0 text-violet" aria-hidden="true" />
                  {output.label}
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={() => routeToIntake({ intent: 'm2m', productHint: 'm2m_t1' })}
            className="mt-9 inline-flex h-12 items-center justify-center gap-2 bg-violet px-6 text-sm font-black uppercase tracking-wide text-chalk transition hover:bg-cyan hover:text-obsidian"
          >
            Turn proof into presence
            <ArrowRight className="size-5" aria-hidden="true" />
          </button>

          <p className="mt-6 text-sm leading-6 text-ash/80">
            Manual-first and draft-only: Macellum prepares the assets, you review and approve before
            anything is published.
          </p>
        </div>
      </div>
    </section>
  );
}
